import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { productCategories } from "@/mocks/products";
import { projectCategories } from "@/mocks/projects";

interface NavChild {
  label: string;
  to: string;
}

interface NavItem {
  label: string;
  to: string;
  children?: NavChild[];
}

const productChildren: NavChild[] = [
  { label: "전체 제품", to: "/products" },
  ...productCategories
    .filter((category) => category !== "전체")
    .map((category) => ({ label: category, to: `/products?category=${encodeURIComponent(category)}` })),
];

const projectChildren: NavChild[] = [
  { label: "전체 프로젝트", to: "/projects" },
  ...projectCategories
    .filter((category) => category !== "전체")
    .map((category) => ({ label: category, to: `/projects?category=${encodeURIComponent(category)}` })),
];

const navItems: NavItem[] = [
  { label: "브랜드", to: "/brand" },
  { label: "제품소개", to: "/products", children: productChildren },
  { label: "프로젝트", to: "/projects", children: projectChildren },
  { label: "고객문의", to: "/contact" },
  { label: "회사소개", to: "/about" },
  { label: "NOTICE", to: "/notice" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openMobileGroup, setOpenMobileGroup] = useState<string | null>(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const [keyword, setKeyword] = useState("");
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setSearchOpen(false);
    setOpenMobileGroup(null);
  }, [location.pathname, location.search]);

  useEffect(() => {
    document.body.style.overflow = mobileOpen || searchOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileOpen, searchOpen]);

  const solid = scrolled || mobileOpen || searchOpen;

  const handleSearch = () => {
    const trimmed = keyword.trim();
    if (!trimmed) return;
    navigate(`/products?q=${encodeURIComponent(trimmed)}`);
    setSearchOpen(false);
    setKeyword("");
  };

  return (
    <header className="fixed inset-x-0 top-0 z-50">
      <div
        className={`hidden lg:block transition-colors duration-500 ${
          solid ? "bg-foreground-950 text-background-200" : "bg-foreground-950/35 text-background-100 backdrop-blur-sm"
        }`}
      >
        <div className="mx-auto flex h-9 max-w-content items-center justify-between px-6 text-[12px] tracking-wide">
          <span className="text-background-200/80">천연석 · 엔지니어드 스톤 · 포세린 종합 석재 브랜드</span>
          <div className="flex items-center gap-5">
            <a href="tel:0517412045" className="cursor-pointer transition-colors hover:text-primary-300">
              <i className="ri-phone-line mr-1.5" />051-741-2045
            </a>
            <span className="text-background-200/40">|</span>
            <span className="text-background-200/80">평일 09:30 - 18:00</span>
          </div>
        </div>
      </div>

      <div
        className={`transition-colors duration-500 ${
          solid ? "border-b border-background-200 bg-background-50/95 backdrop-blur-md" : "bg-transparent"
        }`}
      >
        <div className="mx-auto flex h-16 max-w-content items-center justify-between gap-4 px-4 md:h-20 md:px-6">
          <Link
            to="/"
            className={`flex cursor-pointer items-center gap-2.5 whitespace-nowrap transition-colors ${
              solid ? "text-foreground-950" : "text-white"
            }`}
          >
            <span className="flex h-9 w-9 items-center justify-center rounded-md bg-primary-500 text-background-50">
              <i className="ri-layout-masonry-line text-lg" />
            </span>
            <span className="flex flex-col leading-none">
              <span className="font-heading text-xl font-bold tracking-tight md:text-2xl">동우스톤</span>
              <span className="font-label text-[9px] tracking-[0.28em] opacity-70">DONGWOO STONE</span>
            </span>
          </Link>

          <nav className="hidden items-center gap-1 lg:flex">
            {navItems.map((item) => (
              <div key={item.label} className="group relative">
                <Link
                  to={item.to}
                  className={`flex cursor-pointer items-center gap-1 whitespace-nowrap rounded-md px-3.5 py-2 text-[15px] font-medium transition-colors ${
                    solid ? "text-foreground-800 hover:text-primary-600" : "text-white/90 hover:text-white"
                  }`}
                >
                  {item.label}
                  {item.children && <i className="ri-arrow-down-s-line text-base opacity-70" />}
                </Link>
                {item.children && (
                  <div className="invisible absolute left-1/2 top-full z-50 w-52 -translate-x-1/2 pt-3 opacity-0 transition-all duration-200 group-hover:visible group-hover:opacity-100">
                    <div className="overflow-hidden rounded-lg border border-background-200 bg-background-50 py-2">
                      {item.children.map((child) => (
                        <Link
                          key={child.label}
                          to={child.to}
                          className="block cursor-pointer px-4 py-2.5 text-sm text-foreground-700 transition-colors hover:bg-secondary-100 hover:text-primary-600"
                        >
                          {child.label}
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </nav>

          <div className="flex items-center gap-1.5">
            <button
              type="button"
              onClick={() => setSearchOpen((prev) => !prev)}
              aria-label="제품 검색"
              className={`flex h-10 w-10 cursor-pointer items-center justify-center rounded-full transition-colors ${
                solid ? "text-foreground-800 hover:bg-secondary-100" : "text-white hover:bg-white/15"
              }`}
            >
              <i className="ri-search-line text-xl" />
            </button>
            <Link
              to="/contact"
              className="hidden cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 md:inline-flex"
            >
              상담 문의
            </Link>
            <button
              type="button"
              onClick={() => setMobileOpen(true)}
              aria-label="메뉴 열기"
              className={`flex h-10 w-10 cursor-pointer items-center justify-center rounded-full transition-colors lg:hidden ${
                solid ? "text-foreground-900 hover:bg-secondary-100" : "text-white hover:bg-white/15"
              }`}
            >
              <i className="ri-menu-line text-2xl" />
            </button>
          </div>
        </div>
      </div>

      {searchOpen && (
        <div className="border-b border-background-200 bg-background-50/98 backdrop-blur-md">
          <div className="mx-auto flex max-w-content items-center gap-3 px-4 py-4 md:px-6">
            <i className="ri-search-line text-xl text-foreground-500" />
            <input
              autoFocus
              value={keyword}
              onChange={(event) => setKeyword(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") handleSearch();
              }}
              placeholder="찾으시는 석재나 제품명을 입력하세요 (예: 카라라, 트래버틴)"
              className="h-11 w-full rounded-md border border-background-300 bg-background-50 px-4 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
            />
            <button
              type="button"
              onClick={handleSearch}
              className="h-11 cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              검색
            </button>
            <button
              type="button"
              onClick={() => setSearchOpen(false)}
              aria-label="검색 닫기"
              className="flex h-11 w-11 cursor-pointer items-center justify-center rounded-md text-foreground-600 transition-colors hover:bg-secondary-100"
            >
              <i className="ri-close-line text-xl" />
            </button>
          </div>
        </div>
      )}

      {mobileOpen && (
        <div className="fixed inset-0 top-0 z-50 flex flex-col bg-background-50 lg:hidden">
          <div className="flex h-16 items-center justify-between border-b border-background-200 px-4">
            <span className="font-heading text-xl font-bold text-foreground-950">동우스톤</span>
            <button
              type="button"
              onClick={() => setMobileOpen(false)}
              aria-label="메뉴 닫기"
              className="flex h-10 w-10 cursor-pointer items-center justify-center rounded-full text-foreground-900 transition-colors hover:bg-secondary-100"
            >
              <i className="ri-close-line text-2xl" />
            </button>
          </div>
          <nav className="flex-1 overflow-y-auto px-4 py-3">
            {navItems.map((item) => (
              <div key={item.label} className="border-b border-background-200">
                {item.children ? (
                  <>
                    <button
                      type="button"
                      onClick={() => setOpenMobileGroup((prev) => (prev === item.label ? null : item.label))}
                      className="flex w-full cursor-pointer items-center justify-between py-4 text-left text-base font-medium text-foreground-900"
                    >
                      {item.label}
                      <i
                        className={`ri-arrow-down-s-line text-xl text-foreground-500 transition-transform ${
                          openMobileGroup === item.label ? "rotate-180" : ""
                        }`}
                      />
                    </button>
                    {openMobileGroup === item.label && (
                      <div className="pb-3 pl-2">
                        {item.children.map((child) => (
                          <Link
                            key={child.label}
                            to={child.to}
                            className="block cursor-pointer rounded-md px-3 py-2.5 text-sm text-foreground-600 transition-colors hover:bg-secondary-100 hover:text-primary-600"
                          >
                            {child.label}
                          </Link>
                        ))}
                      </div>
                    )}
                  </>
                ) : (
                  <Link
                    to={item.to}
                    className="block cursor-pointer py-4 text-base font-medium text-foreground-900 transition-colors hover:text-primary-600"
                  >
                    {item.label}
                  </Link>
                )}
              </div>
            ))}
            <Link
              to="/contact"
              className="mt-5 flex cursor-pointer items-center justify-center gap-2 rounded-md bg-primary-500 px-4 py-3.5 text-sm font-medium text-background-50"
            >
              <i className="ri-customer-service-2-line" /> 상담 문의하기
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
}